'use strict';

(function (global) {
  /**
   * Класс встроенной подсказки.
   *
   * @class WebAccess.components.notifications.ReactInlineHint
   * @extend BaseReactObject
   * @since 5.6.0.1300
   */
  class ReactInlineHint extends BaseReactObject {
    static get EVENT() {
      // Событие, срабатывающее при изменении модели встроенной подсказки.
      return Object.assign(
        {},
        BaseReactObject.EVENT,
        {
          CHANGE_STATE: 'ReactInlineHintChange',
        },
      );
    }
    /**
     * Конструктор.
     *
     * @ignore
     * @method WebAccess.components.notifications.ReactInlineHint#init
     * @param {object} model Модель панели действий.
     * @override
     * @see BaseReactObject#init
     */
    constructor (model) {
      super(model);
      /**
       * Имя класса.
       *
       * @private
       * @property WebAccess.components.notifications.ReactInlineHint#className
       * @override
       * @see BaseReactObject#className
       */
      this.className = 'ReactInlineHint';
      this._model.extended = {
        hide: () => {
          this.hide();
        }
      };
    }

    /**
     * Показывает встроенную подсказку с предупреждением.
     *
     * @method WebAccess.components.notifications.ReactInlineHint#showWarning
     * @param {string} title Основной текст предупреждения.
     * @param  {string} [description = ''] Текст, объясняющий суть предупреждения.
     * @returns {WebAccess.components.notifications.ReactInlineHint} Текущий объект сообщения.
     */
    showWarning(title, description = '') {
      const inlineHintParams = {
        description,
        type: MESSAGE_TYPE.WARNING,
      };
      return this.show(title, inlineHintParams);
    }

    /**
     * Показывает встроенную подсказку с текстом ошибки.
     *
     * @method WebAccess.components.notifications.ReactInlineHint#showError
     * @param {string} title Основной текст ошибки.
     * @param  {string} [description = ''] Текст, объясняющий причину ошибки.
     * @returns {WebAccess.components.notifications.ReactInlineHint} Текущий объект сообщения.
     */
    showError(title, description = '') {
      const inlineHintParams = {
        description,
        type: MESSAGE_TYPE.ERROR,
      };
      return this.show(title, inlineHintParams);
    }

    /**
     * Показывает встроенную подсказку с информационным сообщением.
     *
     * @method WebAccess.components.notifications.ReactInlineHint#showInfo
     * @param {string} title Основной текст сообщения.
     * @param  {string} [description = ''] Текст, объясняющий суть подсказки.
     * @returns {WebAccess.components.notifications.ReactInlineHint} Текущий объект сообщения.
     */
    showInfo(title, description = '') {
      const inlineHintParams = {
        description,
        type: MESSAGE_TYPE.INFO,
      };
      return this.show(title, inlineHintParams);
    }

    /**
     * Показывает подсказку в диалоге.
     *
     * @method WebAccess.components.notifications.ReactInlineHint#showInDialog
     * @param {string} dialogId ИД диалога.
     * @param {string} description Основной текст подсказки.
     * @param {string} [details = ''] Дополнительная информация.
     * @param {MESSAGE_TYPE} [messageType] Тип сообщения.
     * @returns {WebAccess.components.notifications.ReactInlineHint} Текущий объект сообщения.
     */
    showInDialog(dialogId, description, details = '', messageType) {
      // В диалогах нет "реактивного" сообщения об ошибке
      // Используем обычное сообщение об ошибке
      (new global.InlineHint()).showInDialog(dialogId, description, details, messageType);
      return this;
    }

    /**
     * Сформировать модель встроенной подсказки.
     *
     * @method WebAccess.components.notifications.ReactInlineHint#_prepareHintModel
     * @param {...*} args Аргументы метода show, из которых будет формироваться модель встроенной подсказки
     * @private
     * @since 5.7.0.2201
     */
    _prepareHintModel(...args) {

      // Начиная с версии 5.6 метод show нужно вызывать так: show(title, params);
      if (typeof args[1] !== 'object' && args.length >= 2) {
        // Приводим аргументы в современный вид и сообщаем о том, что по-старому пользоваться не рекомендуется
        this._warnAboutDeprecatedMethod('WA.CMP.NTF.InlineHint.show', 'Устаревший вариант использования аргументов метода {0}');
        Object.assign(this._model, {
          description: args[1],
          priority: args[3],
          type: args[2],
        });
      } else {
        Object.assign(this._model, args[1]);
        // Раньше вместо description был message
        if (typeof this._model.message === 'string') {
          this._warnAboutDeprecatedMethod('WA.CMP.NTF.InlineHint.show', 'Устаревший вариант использования аргументов метода {0}');
          this._model.description = this._model.message;
          delete this._model.message;
        }
      }

      // Устанавливаем значения по умолчанию.
      if (typeof this._model.description !== 'string') {
        this._model.description = '';
      }
      if (!Object.values(INLINE_HINT_PRIORITY).includes(this._model.priority)) {
        this._model.priority = INLINE_HINT_PRIORITY.SECONDARY;
      }
      if (!Object.values(MESSAGE_TYPE).includes(this._model.type)) {
        this._model.type = MESSAGE_TYPE.ERROR;
      }

      let title = args[0].toString();

      // Пробуем распарсить JSON, на случай, если в метод первым параметром был передан JSON-объект
      try {
        let jsonObject = JSON.parse(title);
        if (jsonObject.Message) {
          title = jsonObject.Message;
        }
      } catch (e) {
        // Игнорируем, если title не является JSON-объектом
      }

      title.trim().replace(/\n/g, '<br>').replaceAll("\u0026quot;", '"');

      if (title.includes('^')) {
        const splitterIndex = title.indexOf('^');
        this._model.description = title.substring(splitterIndex + 1).fromISBuilderToNormal();
        title = title.substring(0, splitterIndex);
      }

      this._model.title = title;
    }

    /**
     * Показывает встроенную подсказку.
     *
     * @method WebAccess.components.notifications.ReactInlineHint#show
     * @param {string} title Основной текст подсказки.
     * @param {string} [params = {}] Дополнительные параметры
     * @param {string} [params.description = ''] Информационный текст
     * @param {string} [params.priority = INLINE_HINT_PRIORITY.SECONDARY] Приоритет встроенной подсказки
     * @param {string} [params.type = MESSAGE_TYPE.ERROR] Тип сообщения
     * @returns {WebAccess.components.notifications.ReactInlineHint} Текущий объект сообщения.
     */
    show(title, params = {}) {
      // Подготавливаем модель подсказки
      this._prepareHintModel(...arguments);
      this.fire(this.constructor.EVENT.CHANGE_STATE, this._model);
      return this;
    }

    /**
     * Скрывает встроенную подсказку.
     *
     * @method WebAccess.components.notifications.ReactInlineHint#hide
     * @returns {WebAccess.components.notifications.ReactInlineHint} Текущий объект сообщения.
     */
    hide() {
      for (let key in this._model) {
        if (this._model.hasOwnProperty(key) && key !== 'extended') {
          delete this._model[key];
        }
      }

      this.fire(this.constructor.EVENT.CHANGE_STATE, this._model);
      return this;
    }
  }

  global.ReactInlineHint = ReactInlineHint;

})(window.WebAccess.components.notifications);